import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { TooltipArrowPosition, TooltipContext, TooltipSize } from '../tooltip-options';
import { AnimationType } from '../../../animations/animations-options';

@Component({
  selector: 'kz-tooltip',
  template: `<kz-shimmer *ngIf="!!tooltipPlaceholder"></kz-shimmer>
  <div (kzClickOutside)="tooltipClickedOutside.emit()" [exceptions]="clickOutsideExceptions"><ng-content></ng-content></div>`,
  styleUrls: ['./kz-tooltip.component.scss'],
  host: {
    '[class]': '"kz-tooltip" + (classes ? " " + classes : "")',
    'role': 'tooltip',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class KzTooltipComponent implements OnInit, OnChanges {
  private _classes: string;

  get classes(): string {
    return this._classes;
  }

  private _loadFromDirective = false;

  get loadFromDirective(): boolean {
    return this._loadFromDirective;
  }

  @Input()
  set loadFromDirective(value: boolean) {
    this._loadFromDirective = value;
  }

  private _tooltipContext: TooltipContext = 'default';

  get tooltipContext(): TooltipContext {
    return this._tooltipContext;
  }

  @Input()
  set tooltipContext(type: TooltipContext) {
    this._tooltipContext = type;
  }

  private _tooltipSize: TooltipSize = 'default';

  get tooltipSize(): TooltipSize {
    return this._tooltipSize;
  }

  @Input()
  set tooltipSize(size: TooltipSize) {
    this._tooltipSize = size;
  }

  private _tooltipArrowPosition: TooltipArrowPosition = 'bottom-center';

  get tooltipArrowPosition(): TooltipArrowPosition {
    return this._tooltipArrowPosition;
  }

  @Input()
  set tooltipArrowPosition(position: TooltipArrowPosition) {
    this._tooltipArrowPosition = position;
    this._updateClasses();
  }


  private _tooltipHideArrow = false;

  get tooltipHideArrow(): boolean {
    return this._tooltipHideArrow;
  }

  @Input()
  set tooltipHideArrow(hide: boolean) {
    this._tooltipHideArrow = hide;
    this._updateClasses();
  }

  private _clickOutsideExceptions: Array<any> = [];

  get clickOutsideExceptions(): Array<any> {
    return this._clickOutsideExceptions;
  }

  @Input()
  set clickOutsideExceptions(clickOutsideExceptions: Array<any>) {
    this._clickOutsideExceptions = clickOutsideExceptions;
  }

  private _tooltipPadding = true;

  get tooltipPadding(): boolean {
    return this._tooltipPadding;
  }

  @Input()
  set tooltipPadding(hasPadding: boolean) {
    this._tooltipPadding = hasPadding;
  }

  private _tooltipPlaceholder: AnimationType;

  get tooltipPlaceholder(): AnimationType {
    return this._tooltipPlaceholder;
  }

  @Input()
  set tooltipPlaceholder(placeholder: AnimationType) {
    this._tooltipPlaceholder = placeholder;
  }

  @Output()
  readonly tooltipMouseLeaved: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  readonly tooltipClickedOutside: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private _elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this._updateClasses();
    this._changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._updateClasses();
    this._changeDetectorRef.detectChanges();
  }

  private _updateClasses() {
    this._classes = '';

    if (this.tooltipContext) {
      this._classes += ` kz-tooltip--${ this.tooltipContext }`;
    }

    if (this.tooltipHideArrow) {
      this._classes += ` kz-tooltip--arrow-none`;
    }

    if (this.tooltipArrowPosition) {
      this._classes += ` kz-tooltip--${ this.tooltipArrowPosition }`;
    }

    if (this.tooltipPadding) {
      this._classes += ` kz-tooltip--padding`;
    }

    if (this.tooltipSize && this.tooltipSize !== 'default') {
      this._classes += ` kz-tooltip-${ this.tooltipSize }`;
    }

    if (this.loadFromDirective) {
      this._classes += ` kz-tooltip-z-index`;
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.tooltipMouseLeaved.emit(true);
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this.tooltipMouseLeaved.emit(false);
  }
}
